import {
  CONFLICT_CARRYOVER_OPTIONS,
  MOVEMENT_DAYS_OPTIONS,
  RECOVERY_DURATION_OPTIONS,
  ROBUST_ASSESSMENT_MODEL,
  ROBUST_ASSESSMENT_SCHEMA_VERSION,
} from "@/components/assessment/robustAssessmentData";

const clamp = (value, min = 1, max = 5) =>
  Math.max(min, Math.min(max, Number(value)));

export const numericAnswer = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= 1 && number <= 5 ? number : null;
};

const mean = (values = []) => {
  const usable = values.map(numericAnswer).filter(Number.isFinite);
  if (!usable.length) return null;
  return usable.reduce((sum, value) => sum + value, 0) / usable.length;
};

const minimum = (values = []) => {
  const usable = values.map(numericAnswer).filter(Number.isFinite);
  return usable.length ? Math.min(...usable) : null;
};

const inverse = (value) => {
  const number = numericAnswer(value);
  return number == null ? null : 6 - number;
};

const scoreForKey = (options, key) =>
  options.find((option) => option.key === key)?.score ?? null;

const band = (value, options = {}) => {
  const { low = "Lower", mid = "Moderate", high = "Higher" } = options;
  if (!Number.isFinite(value)) return "Not enough information";
  if (value <= 2.4) return low;
  if (value >= 3.8) return high;
  return mid;
};

const unique = (values = []) => [...new Set(values.filter(Boolean))];
const atLeast = (value, threshold) =>
  Number.isFinite(value) && value >= threshold;
const atMost = (value, threshold) =>
  Number.isFinite(value) && value <= threshold;
const selected = (answers, section, key) =>
  Array.isArray(answers?.[section]?.[key]) ? answers[section][key] : [];

export const isRobustAssessment = (answers = {}) =>
  Number(answers?.meta?.assessmentSchemaVersion) >= ROBUST_ASSESSMENT_SCHEMA_VERSION ||
  answers?.meta?.assessmentModel === ROBUST_ASSESSMENT_MODEL;

export const shouldAskConflictCarryover = (answers = {}) => {
  const worsening = selected(answers, "recent", "worseningFactors");
  const directions = selected(answers, "expression", "reactionDirections");
  return (
    worsening.includes("interpersonal conflict") ||
    directions.includes("confront or approach")
  );
};

export const shouldAskCoachingResponse = (answers = {}) =>
  selected(answers, "connection", "helpfulSettings").includes("1:1 coaching") ||
  selected(answers, "activity", "currentActivities").includes("1:1 coaching");

export const shouldAskSharedActivity = (answers = {}) => {
  const settings = selected(answers, "connection", "helpfulSettings");
  return settings.some((item) =>
    [
      "with a close friend, partner, or family member",
      "1:1 coaching",
      "small group",
      "group class or team activity",
      "larger group",
    ].includes(item),
  );
};

export function buildRobustBaselineVector(answers = {}) {
  const recoveryDuration = scoreForKey(
    RECOVERY_DURATION_OPTIONS,
    answers.control?.recoveryDuration,
  );
  const conflictCarryover = scoreForKey(
    CONFLICT_CARRYOVER_OPTIONS,
    answers.connection?.conflictCarryover,
  );
  const movementDays = scoreForKey(
    MOVEMENT_DAYS_OPTIONS,
    answers.activity?.movementDays,
  );

  return {
    model: ROBUST_ASSESSMENT_MODEL,
    version: ROBUST_ASSESSMENT_SCHEMA_VERSION,
    temporalContext: {
      recentChange: answers.recent?.recentChange || null,
      recentPatterns: selected(answers, "recent", "recentPatterns"),
      changeTiming: answers.recent?.changeTiming || null,
      helpfulFactors: selected(answers, "recent", "helpfulFactors"),
      worseningFactors: selected(answers, "recent", "worseningFactors"),
      context: String(answers.recent?.context || "").trim() || null,
    },
    detection: {
      stateDetection: numericAnswer(answers.mobilization?.stateDetection),
      interoceptiveClarity: numericAnswer(
        answers.mobilization?.interoceptiveClarity,
      ),
    },
    mobilization: {
      sensitivity: numericAnswer(answers.mobilization?.mobilizationSensitivity),
      magnitude: numericAnswer(answers.mobilization?.mobilizationMagnitude),
      availableEnergy: numericAnswer(answers.mobilization?.availableEnergy),
      actionPressure: numericAnswer(answers.mobilization?.actionPressure),
      mobilizationCapacity: numericAnswer(answers.control?.mobilizationCapacity),
    },
    control: {
      behavioralControl: numericAnswer(answers.control?.behavioralControl),
      stopChangeControl: numericAnswer(answers.control?.stopChangeControl),
      cognitiveCapture: numericAnswer(answers.control?.cognitiveCapture),
      cognitiveFlexibility: inverse(answers.control?.cognitiveCapture),
    },
    recovery: {
      recoveryInitiation: numericAnswer(answers.control?.recoveryInitiation),
      recoveryDuration,
      recoveryDurationKey: answers.control?.recoveryDuration || null,
    },
    expression: {
      emotions: selected(answers, "expression", "emotions"),
      bodySignals: selected(answers, "expression", "bodySignals"),
      cognitiveSignals: selected(answers, "expression", "cognitiveSignals"),
      reactionDirections: selected(
        answers,
        "expression",
        "reactionDirections",
      ),
    },
    environment: {
      difficultEnvironments: selected(
        answers,
        "environment",
        "difficultEnvironments",
      ),
      sensitivity: numericAnswer(answers.environment?.effectMagnitude),
      control: numericAnswer(answers.environment?.environmentalControl),
      helpfulEnvironments: selected(
        answers,
        "environment",
        "helpfulEnvironments",
      ),
    },
    connection: {
      helpfulSettings: selected(answers, "connection", "helpfulSettings"),
      coachingResponse: numericAnswer(answers.connection?.coachingResponse),
      supportAvailability: numericAnswer(
        answers.connection?.supportAvailability,
      ),
      socialEvaluationSensitivity: numericAnswer(
        answers.connection?.socialEvaluation,
      ),
      conflictCarryover,
      conflictCarryoverKey: answers.connection?.conflictCarryover || null,
      sharedActivityBenefit: numericAnswer(
        answers.connection?.sharedActivityBenefit,
      ),
    },
    capacity: {
      goalSupport: numericAnswer(answers.capacity?.goalSupport),
      movementFreedom: numericAnswer(answers.capacity?.movementFreedom),
      movementControl: numericAnswer(answers.capacity?.movementControl),
      trainingTolerance: numericAnswer(answers.capacity?.trainingTolerance),
      activityFamiliarity: numericAnswer(answers.capacity?.activityFamiliarity),
      recentLoadManageability: numericAnswer(
        answers.capacity?.recentLoadManageability,
      ),
    },
    resources: {
      sleepRestoration: numericAnswer(answers.resources?.sleepRestoration),
      sleepConsistency: numericAnswer(answers.resources?.sleepConsistency),
      nourishment: numericAnswer(answers.resources?.nourishmentResource),
      hydration: numericAnswer(answers.resources?.hydrationResource),
      recoveryOpportunity: numericAnswer(
        answers.resources?.recoveryOpportunity,
      ),
      routineFit: numericAnswer(answers.resources?.routineFit),
    },
    activity: {
      currentActivities: selected(answers, "activity", "currentActivities"),
      movementDays,
      movementDaysKey: answers.activity?.movementDays || null,
      familiarActivities: selected(
        answers,
        "activity",
        "familiarActivities",
      ),
      preserveActivities: selected(
        answers,
        "activity",
        "preserveActivities",
      ),
    },
  };
}

export function buildRobustScores(vector) {
  const detection = mean([
    vector.detection.stateDetection,
    vector.detection.interoceptiveClarity,
  ]);
  const mobilizationDemand = mean([
    vector.mobilization.sensitivity,
    vector.mobilization.magnitude,
    vector.mobilization.actionPressure,
  ]);
  const availableEnergy = vector.mobilization.availableEnergy;
  const control = mean([
    vector.control.behavioralControl,
    vector.control.stopChangeControl,
    vector.control.cognitiveFlexibility,
  ]);
  const recovery = mean([
    vector.recovery.recoveryInitiation,
    vector.recovery.recoveryDuration,
  ]);
  const environmentFit = mean([
    inverse(vector.environment.sensitivity),
    vector.environment.control,
  ]);
  const connectionResources = mean([
    vector.connection.coachingResponse,
    vector.connection.supportAvailability,
    inverse(vector.connection.socialEvaluationSensitivity),
    vector.connection.conflictCarryover,
    vector.connection.sharedActivityBenefit,
  ]);
  const capacity = mean(Object.values(vector.capacity));
  const capacityCeiling = minimum([
    vector.capacity.movementFreedom,
    vector.capacity.movementControl,
    vector.capacity.trainingTolerance,
    vector.capacity.activityFamiliarity,
    vector.capacity.recentLoadManageability,
  ]);
  const resources = mean(Object.values(vector.resources));

  return {
    detection,
    mobilizationDemand,
    availableEnergy,
    control,
    recovery,
    environmentFit,
    connectionResources,
    capacity,
    capacityCeiling,
    resources,
    confidence: calculateConfidence(vector),
  };
}

function calculateConfidence(vector) {
  const expected = [
    ...Object.values(vector.detection),
    ...Object.values(vector.mobilization),
    ...Object.values(vector.control).filter((_, index) => index !== 3),
    vector.recovery.recoveryInitiation,
    vector.recovery.recoveryDuration,
    vector.environment.sensitivity,
    vector.environment.control,
    ...Object.values(vector.capacity),
    ...Object.values(vector.resources),
  ];
  const usable = expected.filter(Number.isFinite).length;
  const ratio = usable / expected.length;
  if (ratio >= 0.88) return "high";
  if (ratio >= 0.68) return "moderate";
  return "limited";
}

const legacyProfileKey = (scores) => {
  if (Number.isFinite(scores.capacityCeiling) && scores.capacityCeiling <= 2.4) {
    return "reduced-capacity";
  }
  if (
    Number.isFinite(scores.mobilizationDemand) &&
    scores.mobilizationDemand >= 3.8 &&
    Number.isFinite(scores.control) &&
    atMost(scores.control, 2.7)
  ) {
    return "high-activation";
  }
  if (
    (Number.isFinite(scores.recovery) && atMost(scores.recovery, 2.7)) ||
    (Number.isFinite(scores.resources) && scores.resources <= 2.6)
  ) {
    return "recovery-focus";
  }
  if (
    [scores.control, scores.recovery, scores.capacity, scores.resources].every(
      (value) => Number.isFinite(value) && value >= 3.8,
    )
  ) {
    return "growth-performance";
  }
  return "regulation-building";
};

const generateHeadline = (scores, vector) => {
  const highMobilization = atLeast(scores.mobilizationDemand, 3.7);
  const lowMobilization = atMost(scores.mobilizationDemand, 2.4);
  const lowControl = atMost(scores.control, 2.7);
  const highControl = atLeast(scores.control, 3.8);
  const lowEnergy = atMost(scores.availableEnergy, 2.4);
  const lowRecovery = atMost(scores.recovery, 2.7);
  const lowResources = atMost(scores.resources, 2.7);
  const highEnvironmentDemand =
    atLeast(vector.environment.sensitivity, 4) &&
    atMost(vector.environment.control, 3);
  const strongCapacity = atLeast(scores.capacity, 3.8);

  if (highEnvironmentDemand && strongCapacity) {
    return "Environmental demand amplifies your nervous-system reaction more than physical capacity limits it.";
  }
  if (highMobilization && lowEnergy) {
    return "Physical activation remains high while usable energy is limited.";
  }
  if (highMobilization && lowControl) {
    return "Your nervous system mobilizes quickly while behavioral control narrows.";
  }
  if (highMobilization && highControl && (lowRecovery || lowResources)) {
    return "Your nervous system mobilizes strongly while recovery resources are limited.";
  }
  if (highMobilization && highControl) {
    return "Your nervous system mobilizes strongly while behavioral control remains available.";
  }
  if (lowMobilization && strongCapacity) {
    return "Your nervous system mobilizes slowly even though physical capacity is available.";
  }
  if (lowRecovery) {
    return "Your nervous-system reaction takes longer to settle after demand has passed.";
  }
  if (
    atLeast(scores.control, 3.8) &&
    atLeast(scores.recovery, 3.8) &&
    atLeast(scores.capacity, 3.8)
  ) {
    return "Your nervous system mobilizes and recovers with reliable flexibility.";
  }
  return "Your nervous system has usable capacity, but the response changes across demand, context, and recovery.";
};

const buildBottomLine = (scores, vector) => {
  const parts = [];
  if (atLeast(scores.mobilizationDemand, 3.7)) {
    parts.push(
      "Your nervous system tends to generate a strong action-ready reaction when demand rises",
    );
  } else if (atMost(scores.mobilizationDemand, 2.4)) {
    parts.push(
      "Your nervous system tends to require more time or a clearer demand before mobilizing",
    );
  } else {
    parts.push(
      "Your nervous system shows a moderate and context-dependent mobilization pattern",
    );
  }

  if (atLeast(scores.control, 3.7)) {
    parts.push("you usually retain meaningful control over what happens next");
  } else if (atMost(scores.control, 2.7)) {
    parts.push("behavioral control can narrow once the reaction is underway");
  } else {
    parts.push("control remains available in some situations more than others");
  }

  if (atMost(scores.recovery, 2.7) || atMost(scores.resources, 2.7)) {
    parts.push(
      "the main limitation appears to be recovery or the resources available to support it",
    );
  } else if (atLeast(vector.environment.sensitivity, 4)) {
    parts.push("environmental demand meaningfully changes how the reaction develops");
  } else if (atMost(scores.capacityCeiling, 2.7)) {
    parts.push("physical capacity currently limits the appropriate relative dose");
  } else {
    parts.push("physical capacity is generally available for familiar, controlled activity");
  }

  return `${parts[0]}; ${parts[1]}; and ${parts[2]}. APOS should preserve what already works, match the physiological direction to the current nervous-system state, and then adjust the relative dose using control, capacity, schedule, and observed response.`;
};

const strengthItems = (scores, vector) => {
  const items = [];
  if (atLeast(scores.detection, 3.8)) items.push("You tend to notice nervous-system changes early enough to create an intervention window.");
  if (atLeast(scores.control, 3.8)) items.push("Behavioral control usually remains available even when the reaction is strong.");
  if (atLeast(scores.recovery, 3.8)) items.push("Recovery can begin reliably once the demand has passed.");
  if (atLeast(scores.capacity, 3.8)) items.push("Familiar physical activity is supported by useful movement control and capacity.");
  if (atLeast(vector.environment.control, 4)) items.push("You can usually change or leave an environment that is increasing demand.");
  if (atLeast(vector.connection.coachingResponse, 4)) items.push("1:1 coaching appears to improve focus, control, or follow-through.");
  vector.temporalContext.helpfulFactors.slice(0, 4).forEach((item) =>
    items.push(`Reported helpful factor: ${item}.`),
  );
  return unique(items).slice(0, 7);
};

const contributorItems = (scores, vector) => {
  const items = [];
  if (atLeast(scores.mobilizationDemand, 3.8)) items.push("Strong or rapid mobilization increases the amount of action pressure that needs to be organized.");
  if (atMost(scores.availableEnergy, 2.4)) items.push("Low usable energy may make a strong nervous-system reaction feel agitated, heavy, or difficult to direct.");
  if (atMost(scores.control, 2.7)) items.push("Reduced behavioral control can narrow the range of responses available once the reaction begins.");
  if (atLeast(vector.control.cognitiveCapture, 4)) items.push("Attention may stay captured after the original demand, prolonging the reaction.");
  if (atMost(scores.recovery, 2.7)) items.push("Recovery begins slowly or clear function takes longer to return.");
  if (atLeast(vector.environment.sensitivity, 4)) items.push("Environmental stimulation meaningfully increases nervous-system demand.");
  if (atLeast(vector.connection.socialEvaluationSensitivity, 4)) items.push("Being watched, compared, or evaluated adds substantial social demand.");
  vector.temporalContext.worseningFactors.slice(0, 5).forEach((item) =>
    items.push(`Reported contributor: ${item}.`),
  );
  return unique(items).slice(0, 8);
};

const priorityItems = (scores, vector) => {
  const priorities = [];
  if (atMost(scores.resources, 2.7)) {
    priorities.push({
      title: "Strengthen recovery resources first",
      body: "Protect sleep, nourishment, hydration, and lower-demand recovery time before adding unnecessary physiological demand.",
    });
  }
  if (atMost(scores.control, 2.7)) {
    priorities.push({
      title: "Preserve behavioral control during demand",
      body: "Use familiar, predictable, stoppable inputs and reduce technical, competitive, or social complexity when the nervous-system reaction is strong.",
    });
  }
  if (atMost(scores.recovery, 2.7)) {
    priorities.push({
      title: "Train the recovery transition",
      body: "Do not end at peak output. Taper deliberately and observe how long it takes for breathing, tension, attention, and useful function to return.",
    });
  }
  if (atLeast(vector.environment.sensitivity, 4) && atMost(vector.environment.control, 3)) {
    priorities.push({
      title: "Change the environment before adding more effort",
      body: "Reducing stimulation or moving settings may lower total demand more effectively than immediately adding another exercise input.",
    });
  }
  if (atLeast(vector.connection.coachingResponse, 4)) {
    priorities.push({
      title: "Use 1:1 coaching when control or follow-through matters",
      body: "Real-time guidance may improve technical control, dose adjustment, attention, and completion of the recovery transition.",
    });
  }
  if (!priorities.length) {
    priorities.push({
      title: "Maintain flexibility across different demands",
      body: "Keep familiar activity, vary one demand at a time, and use before-to-after observations to learn which dose works best in comparable nervous-system states.",
    });
  }
  return priorities.slice(0, 3);
};

const noChangeItems = (scores, vector) => {
  const items = [];
  if (atLeast(scores.capacity, 3.7)) items.push("Your current physical capacity does not need to be treated as the primary problem.");
  if (atLeast(scores.control, 3.7)) items.push("A strong nervous-system reaction does not automatically mean you have lost behavioral control.");
  if (vector.activity.preserveActivities.length) {
    items.push(`Keep the activities you asked APOS to preserve: ${vector.activity.preserveActivities.join(", ")}.`);
  }
  if (atLeast(scores.mobilizationDemand, 3.7)) items.push("Mobilization itself does not need to be eliminated; it needs an appropriate direction, dose, and recovery transition.");
  if (!items.length) items.push("Do not replace familiar activities automatically. Adjust only the variables that the current nervous-system state, capacity, schedule, and observed response indicate.");
  return items.slice(0, 4);
};

const baselineInput = (scores, vector) => {
  if (atLeast(scores.mobilizationDemand, 3.7) && atLeast(scores.availableEnergy, 3.5) && atLeast(scores.control, 3.4)) {
    return {
      title: "Organized familiar physical output followed by a deliberate recovery transition",
      direction: "Use available mobilization productively rather than trying to erase it.",
      dose: "Begin at a moderate-to-vigorous relative dose only when current readiness, calendar load, and activity-specific familiarity support it.",
    };
  }
  if (atLeast(scores.mobilizationDemand, 3.7) && atMost(scores.control, 3)) {
    return {
      title: "Simple, predictable, easy-to-stop movement",
      direction: "Reduce complexity while giving the nervous-system reaction an organized physical direction.",
      dose: "Use a low-to-moderate relative dose with clear start, stop, and taper points; consider 1:1 coaching when it improves control.",
    };
  }
  if (atLeast(scores.mobilizationDemand, 3.7) && (atMost(scores.availableEnergy, 2.6) || atMost(scores.resources, 2.7))) {
    return {
      title: "Lower-demand rhythmic movement or environmental change",
      direction: "Support the nervous system without treating activation as proof that high exercise output is available.",
      dose: "Use a low relative dose, preserve energy, and prioritize nourishment, hydration, or recovery where indicated.",
    };
  }
  if (atMost(scores.mobilizationDemand, 2.4) && atMost(scores.availableEnergy, 2.6)) {
    return {
      title: "Accessible activation through light, familiar movement",
      direction: "Increase circulation and engagement without creating another draining demand.",
      dose: "Use a minimal-to-low relative dose, natural light or outdoor access when helpful, and stop before fatigue rises.",
    };
  }
  return {
    title: "Familiar activity matched to the current nervous-system state",
    direction: "Preserve useful capacity and adjust the activity according to what the nervous system needs now.",
    dose: "Let the current check-in, scheduled demands, and personal response history determine the relative dose.",
  };
};

export function buildRobustNervousSystemProfile(answers = {}) {
  const vector = buildRobustBaselineVector(answers);
  const scores = buildRobustScores(vector);
  const headline = generateHeadline(scores, vector);
  const legacyKey = legacyProfileKey(scores);

  const groups = [
    {
      key: "detection",
      title: "Detection",
      description: "How early and clearly nervous-system changes become noticeable.",
      metrics: [
        ["Early detection", vector.detection.stateDetection],
        ["Physical-signal clarity", vector.detection.interoceptiveClarity],
      ],
    },
    {
      key: "mobilization",
      title: "Mobilization",
      description: "How readily and strongly the body prepares for action, and how much usable energy is present.",
      metrics: [
        ["Mobilization sensitivity", vector.mobilization.sensitivity],
        ["Reaction magnitude", vector.mobilization.magnitude],
        ["Usable energy", vector.mobilization.availableEnergy],
        ["Action pressure", vector.mobilization.actionPressure],
      ],
    },
    {
      key: "control",
      title: "Behavioral control",
      description: "How much control, stopping ability, and attentional flexibility remain during demand.",
      metrics: [
        ["Retained control", vector.control.behavioralControl],
        ["Stop/change control", vector.control.stopChangeControl],
        ["Attentional flexibility", vector.control.cognitiveFlexibility],
      ],
    },
    {
      key: "recovery",
      title: "Transition and recovery",
      description: "How reliably recovery begins and how quickly clear function returns.",
      metrics: [
        ["Recovery initiation", vector.recovery.recoveryInitiation],
        ["Recovery duration", vector.recovery.recoveryDuration],
      ],
    },
    {
      key: "environment",
      title: "Environment",
      description: "How strongly environments alter nervous-system demand and how much control is available over the setting.",
      metrics: [
        ["Environmental tolerance", inverse(vector.environment.sensitivity)],
        ["Environmental control", vector.environment.control],
      ],
    },
    {
      key: "connection",
      title: "Connection and social context",
      description: "How coaching, support, social evaluation, conflict, and shared activity influence the reaction.",
      metrics: [
        ["1:1 coaching response", vector.connection.coachingResponse],
        ["Support availability", vector.connection.supportAvailability],
        ["Social-evaluation tolerance", inverse(vector.connection.socialEvaluationSensitivity)],
        ["Conflict recovery", vector.connection.conflictCarryover],
        ["Shared-activity benefit", vector.connection.sharedActivityBenefit],
      ],
    },
    {
      key: "capacity",
      title: "Physical capacity",
      description: "Movement freedom, control, familiarity, training tolerance, and recent load.",
      metrics: [
        ["Physical-goal support", vector.capacity.goalSupport],
        ["Movement freedom", vector.capacity.movementFreedom],
        ["Movement control", vector.capacity.movementControl],
        ["Training tolerance", vector.capacity.trainingTolerance],
        ["Activity familiarity", vector.capacity.activityFamiliarity],
        ["Recent-load manageability", vector.capacity.recentLoadManageability],
      ],
    },
    {
      key: "resources",
      title: "Recovery resources",
      description: "Sleep, nourishment, hydration, recovery opportunity, and routine fit.",
      metrics: [
        ["Sleep restoration", vector.resources.sleepRestoration],
        ["Sleep consistency", vector.resources.sleepConsistency],
        ["Nourishment", vector.resources.nourishment],
        ["Hydration", vector.resources.hydration],
        ["Recovery opportunity", vector.resources.recoveryOpportunity],
        ["Routine fit", vector.resources.routineFit],
      ],
    },
  ];

  return {
    model: ROBUST_ASSESSMENT_MODEL,
    version: ROBUST_ASSESSMENT_SCHEMA_VERSION,
    legacyProfileKey: legacyKey,
    headline,
    title: headline,
    bottomLine: buildBottomLine(scores, vector),
    vector,
    scores,
    groups,
    contributors: contributorItems(scores, vector),
    protectiveFactors: strengthItems(scores, vector),
    priorities: priorityItems(scores, vector),
    noChangeNeeded: noChangeItems(scores, vector),
    baselineInput: baselineInput(scores, vector),
    confidence: scores.confidence,
    expression: vector.expression,
    temporalContext: vector.temporalContext,
    bandLabels: {
      detection: band(scores.detection, {
        low: "Later detection",
        mid: "Variable detection",
        high: "Earlier detection",
      }),
      mobilization: band(scores.mobilizationDemand, {
        low: "Lower mobilization",
        mid: "Moderate mobilization",
        high: "Higher mobilization",
      }),
      control: band(scores.control, {
        low: "Control often narrows",
        mid: "Context-dependent control",
        high: "Control usually available",
      }),
      recovery: band(scores.recovery, {
        low: "Slower recovery",
        mid: "Variable recovery",
        high: "Reliable recovery",
      }),
      capacity: band(scores.capacity, {
        low: "Limited current capacity",
        mid: "Moderate current capacity",
        high: "Strong current capacity",
      }),
      resources: band(scores.resources, {
        low: "Limited recovery resources",
        mid: "Variable recovery resources",
        high: "Reliable recovery resources",
      }),
    },
  };
}

export function robustCompatibilityDimensions(answers = {}) {
  const profile = buildRobustNervousSystemProfile(answers);
  return {
    state: mean([
      profile.scores.detection,
      profile.scores.control,
      profile.scores.recovery,
      profile.scores.availableEnergy,
    ]) || 3,
    inputs: profile.scores.resources || 3,
    capacity: profile.scores.capacity || 3,
  };
}

export function buildLegacyAnswersFromRobust(answers = {}) {
  const profile = buildRobustNervousSystemProfile(answers);
  const vector = profile.vector;
  const activitySelections = vector.activity.currentActivities;
  const preferredActivities = activitySelections.includes("no regular activity right now")
    ? ["none"]
    : activitySelections.length
      ? activitySelections
      : ["none"];

  return {
    ...answers,
    state: {
      usableEnergyReliability: vector.mobilization.availableEnergy || 3,
      mobilizationFit: mean([
        inverse(vector.mobilization.sensitivity),
        vector.control.behavioralControl,
        vector.mobilization.mobilizationCapacity,
      ]) || 3,
      behavioralChoice: vector.control.behavioralControl || 3,
      cognitiveFlexibility: vector.control.cognitiveFlexibility || 3,
      transitionFlexibility: vector.recovery.recoveryInitiation || 3,
      recoveryEfficiency: vector.recovery.recoveryDuration || 3,
      bodySignalClarity: vector.detection.interoceptiveClarity || 3,
      sleepRestoration: vector.resources.sleepRestoration || 3,
    },
    inputs: {
      movement: vector.activity.movementDays || 3,
      exercise: vector.capacity.goalSupport || 3,
      outdoors: vector.environment.helpfulEnvironments.includes(
        "outdoor space or natural light",
      )
        ? 4
        : 3,
      nutrition: vector.resources.nourishment || 3,
      hydration: vector.resources.hydration || 3,
      screens: vector.environment.difficultEnvironments.includes(
        "screens and notifications",
      )
        ? inverse(vector.environment.sensitivity) || 3
        : 3,
      connection: vector.connection.supportAvailability || 3,
      routine: vector.resources.routineFit || 3,
    },
    capacity: {
      activity: vector.capacity.goalSupport || 3,
      injury: vector.capacity.movementFreedom || 3,
      confidence: vector.capacity.movementControl || 3,
      tolerance: vector.capacity.trainingTolerance || 3,
      familiarity: vector.capacity.activityFamiliarity || 3,
      recentLoad: vector.capacity.recentLoadManageability || 3,
    },
    behaviors: vector.expression.reactionDirections,
    schedule: {
      ...(answers.schedule || {}),
      preferredActivities,
      familiarActivities: vector.activity.familiarActivities,
      preserveActivities: vector.activity.preserveActivities,
      socialPreference:
        vector.connection.helpfulSettings[0] || "it depends on the situation",
      resources: vector.environment.helpfulEnvironments,
      scheduledActivities: answers.schedule?.scheduledActivities || [],
    },
    robustProfile: profile,
  };
}
